import type { Graphics } from 'pixi.js';
import type { GateShape } from '../core/chart';
import type { Painter } from './art/paint';
import type { BurstSpec } from './burst';

export type Blend = 'normal' | 'add';

export interface Palette {
  readonly bg: number;
  readonly ink: number;
  readonly rim: number;
  readonly held: number;
  readonly heldShade: number;
  readonly heldLight: number;
  readonly incoming: Readonly<Record<GateShape, number>>;
  readonly spot: number;
  readonly path: number;
  readonly text: number;
  readonly sparks: readonly number[];
}

export interface Body {
  readonly shape: GateShape;
  readonly x: number;
  readonly y: number;
  readonly radius: number;
  readonly rotation: number;
  readonly scaleX: number;
  readonly scaleY: number;
}

export interface Kit {
  readonly g: Graphics;
  readonly pal: Palette;
  readonly outline: number;
  readonly unit: number;
  readonly time: number;
  readonly reduced: boolean;
  readonly frame: number;
  trace(body: Body, dx?: number, dy?: number, grow?: number): void;
  inkRing(body: Body, width: number, seed: number, amp: number, color: number, alpha: number): void;
  hatch(body: Body, angle: number, spacing: number, depth: number, color: number, alpha: number, width: number): void;
  highlight(body: Body, alpha: number): void;
  arcLine(body: Body, radius: number, a0: number, a1: number): void;
  rimLight(body: Body, alpha: number): void;
  bands(body: Body, color: number, alpha: number): void;
  terminator(body: Body, color: number, alpha: number): void;
  plain(body: Body, fill: number, alpha: number, width: number): void;
  ringDots(body: Body, radius: number, count: number, spin: number, draw: (x: number, y: number, i: number, a: number) => void): void;
}

export interface WorldDress {
  incomingCircle(k: Kit, body: Body, color: number, alpha: number): void;
  mineCircle(k: Kit, body: Body, alpha: number, width: number, fill: number, seed: number): void;
  target(k: Kit, body: Body, alpha: number, spread: number, reach: number, spin: number, edge: number): void;
  pip(k: Kit, x: number, y: number, radius: number, on: boolean, last: boolean, alpha: number, width: number): void;
  path(k: Kit, points: readonly number[], count: number, alpha: number): void;
}

export interface Milestone {
  readonly x: number;
  readonly y: number;
  readonly r: number;
  readonly width: number;
  readonly fade: number;
  readonly grow: number;
  readonly unit: number;
}

export interface WorldFx {
  bit(g: Graphics, x: number, y: number, size: number, i: number, color: number, alpha: number, rot: number): void;
  milestone(g: Graphics, m: Milestone, pal: Palette): void;
  trail(g: Graphics, x: number, y: number, size: number, angle: number): void;
}

export interface World {
  readonly id: string;
  readonly name: string;
  readonly seed: string;
  readonly legacy: boolean;
  readonly hue: number;
  readonly ceil: number;
  readonly font: string;
  readonly fontFile: string;
  readonly fontWeight: string;
  readonly fontLicense: string;
  readonly dark: boolean;
  readonly boil: boolean;
  readonly glow: Blend;
  readonly glowAlpha: number;
  readonly fillAlpha: number;
  readonly palette: Palette;
  readonly burst: BurstSpec;
  readonly dress: WorldDress;
  readonly fx: WorldFx;
  paint(p: Painter, world: World): void;
}

export function hexOf(color: number): string {
  return `#${(color & 0xffffff).toString(16).padStart(6, '0')}`;
}

export function luminance(color: number): number {
  const channel = (shift: number): number => {
    const c = ((color >> shift) & 0xff) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(16) + 0.7152 * channel(8) + 0.0722 * channel(0);
}

export function contrast(a: number, b: number): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

export function redRatio(color: number): number {
  const r = (color >> 16) & 0xff;
  const sum = r + ((color >> 8) & 0xff) + (color & 0xff);
  return sum === 0 ? 0 : r / sum;
}

export function hsl(h: number, s: number, l: number): number {
  const sat = Math.max(0, Math.min(100, s)) / 100;
  const lig = Math.max(0, Math.min(100, l)) / 100;
  const k = (n: number): number => (n + h / 30) % 12;
  const a = sat * Math.min(lig, 1 - lig);
  const f = (n: number): number => Math.round(255 * (lig - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1))));
  return (f(0) << 16) | (f(8) << 8) | f(4);
}

export function sceneSeed(seed: string, level: number): string {
  return `look-${seed}-${String(level)}`;
}
